import { useNavigation } from '@react-navigation/native'
import { Box, Text } from 'native-base'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { Platform } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useFilterOffersBottomSheet } from '~bottomSheets'
import {
  FiltersBar,
  OffersList,
  Spacer,
  ScreenHeaderWithPoints,
  LoadingState,
  RewardsPopup,
} from '~components'
import { ASYNC_STORAGE_KEYS, WEB } from '~constants'
import { useDeviceInfo, useGetFilteredOffers, useTheme, useTranslation } from '~hooks'
import { useGetUser } from '~query-hooks'
import { Routes } from '~types/routesNames'
import { secureStore } from '~utils'

export const OffersListScreen = (_: OffersListScreenProps): JSX.Element => {
  const { t } = useTranslation()
  const { colors, space } = useTheme()
  const { user, isLoadingUser, isFetchingUser, userStatus } = useGetUser()
  const { isWeb } = useDeviceInfo()
  const navigation = useNavigation()

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingOffers,
    isRefetchingOffers,
    offers,
    refetchOffers,
  } = useGetFilteredOffers()

  const { presentFilterBottomSheet, filterBottomSheetComponent } = useFilterOffersBottomSheet()
  const [showRewardsPopup, setShowRewardsPopup] = useState(false)

  // Show rewards popup only once if user hasn't completed their profile
  useEffect(() => {
    // Don't check popup until user data is loaded
    if (isLoadingUser || isFetchingUser || userStatus !== 'success' || !user?.email) {
      return
    }

    const checkAndShowPopup = async () => {
      // Check if popup has already been shown
      const popupShown = await secureStore.getItem(ASYNC_STORAGE_KEYS.REWARDS_POPUP_SHOWN)

      if (popupShown === 'true') {
        // Popup already shown, don't show again
        return
      }

      const { basicInfo } = user || {}
      const { city, state, cityId, gender, stateId, zipCode, countryId } = basicInfo || {}

      // Show popup only if ALL fields are missing (if any one is present, don't show popup)
      if (!isFetchingUser && userStatus === 'success') {
        if (!city && !state && !cityId && !gender && !stateId && !zipCode && !countryId) {
          setShowRewardsPopup(true)
        }
      }
    }

    checkAndShowPopup()
  }, [user, isLoadingUser, isFetchingUser, userStatus])

  const handleCloseRewardsPopup = useCallback(async () => {
    setShowRewardsPopup(false)
    // Mark popup as shown so it won't appear again
    await secureStore.setItem(ASYNC_STORAGE_KEYS.REWARDS_POPUP_SHOWN, 'true')
  }, [])

  const handleCompleteRewards = useCallback(async () => {
    setShowRewardsPopup(false)
    // Mark popup as shown so it won't appear again
    await secureStore.setItem(ASYNC_STORAGE_KEYS.REWARDS_POPUP_SHOWN, 'true')
    // Optionally show a success message or navigate somewhere
  }, [])
  const componentStyle = useMemo(
    () => ({
      backgroundColor: colors.background,
      flex: 1,
      paddingTop: space['8'],
    }),
    [colors.background, space]
  )
  const fetchNextPageHandler = useCallback(() => {
    return hasNextPage ? fetchNextPage() : undefined
  }, [fetchNextPage, hasNextPage])

  const handleNavigateToProfile = useCallback(() => {
    if (isWeb || Platform.OS === WEB) {
      // On web, navigate directly to UserProfile screen
      navigation.navigate(Routes.UserProfile as never)
    } else {
      // On mobile, get the parent tab navigator and navigate to UserStack tab
      const parent = navigation.getParent()
      if (parent) {
        parent.navigate(Routes.UserStack as never)
      }
    }
  }, [navigation, isWeb])

  return (
    <SafeAreaView edges={['top']} style={componentStyle}>
      <Box flex={1}>
        <ScreenHeaderWithPoints title={t('common.offers')} />
        <Box px="4" pb="2">
          <Text color="offMain" fontSize="sm">
            {t('offers_list_screen.curated_subtitle_part1')}{' '}
            <Text color="actionBlue" fontSize="sm" underline onPress={handleNavigateToProfile}>
              {t('offers_list_screen.update_details_link')}
            </Text>{' '}
            {t('offers_list_screen.curated_subtitle_part2')}
          </Text>
        </Box>
        <>
          <Spacer y="4" />
          <Box pl="4">
            <FiltersBar onPress={presentFilterBottomSheet} />
          </Box>
          <Spacer y="2" />
          {isLoadingOffers ? (
            <LoadingState />
          ) : (
            <OffersList
              ListFooterComponent={
                isFetchingNextPage ? (
                  <Box pt="4">
                    <LoadingState />
                  </Box>
                ) : undefined
              }
              offers={offers}
              onEndReached={fetchNextPageHandler}
              onRefresh={refetchOffers}
              presentFilterBottomSheet={presentFilterBottomSheet}
              refreshing={isRefetchingOffers}
            />
          )}
        </>

        {filterBottomSheetComponent}

        <RewardsPopup
          isVisible={showRewardsPopup}
          onClose={handleCloseRewardsPopup}
          onComplete={handleCompleteRewards}
          user={user}
        />
      </Box>
    </SafeAreaView>
  )
}
